import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getOffer, convertOfferToInvoice, updateOfferStatus, recordOfferPayment } from '../services/offerService';
import useCompanyInfo from '../../../hooks/useCompanyInfo';
import { toast } from 'react-toastify';
import {
  Box, Button, Typography, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Divider, Chip, Container, Alert, AlertTitle,
  Menu, MenuItem, ListItemIcon, ListItemText, Stepper, Step, StepLabel, StepConnector,
  stepConnectorClasses, alpha, CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import TransformIcon from '@mui/icons-material/Transform';
import DraftsIcon from '@mui/icons-material/Drafts';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PaymentIcon from '@mui/icons-material/Payment';
import DiscountIcon from '@mui/icons-material/Discount';
import PaymentsIcon from '@mui/icons-material/Payments';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import { useTranslation } from "react-i18next";
import { OFFER_STATUS, getStatusColor, getStatusLabel, canConvertOffer, canEditOffer } from '../../../constants/offerConstants';

// ✅ Custom Stepper Connector with dynamic colors
const ColorlibConnector = styled(StepConnector)(({ theme, ownerState }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient(95deg, #667eea 0%, #764ba2 100%)',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient(95deg, #4caf50 0%, #388e3c 100%)',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
    borderRadius: 1,
  },
}));

// ✅ Custom Step Icon
const ColorlibStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#ccc',
  zIndex: 1,
  color: '#fff',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  ...(ownerState.active && {
    backgroundImage: 'linear-gradient(136deg, #667eea 0%, #764ba2 100%)',
    boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
  }),
  ...(ownerState.completed && {
    backgroundImage: 'linear-gradient(136deg, #4caf50 0%, #388e3c 100%)',
  }),
}));

function ColorlibStepIcon(props) {
  const { active, completed, className, icon } = props;

  const icons = {
    1: <DraftsIcon />,
    2: <SendIcon />,
    3: <CheckCircleIcon />,
    4: <TransformIcon />,
  };

  return (
    <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
      {icons[String(icon)]}
    </ColorlibStepIconRoot>
  );
}

// ✅ Payment method options
const PAYMENT_METHOD_OPTIONS = {
  'Cash': { label: 'Cash', icon: '💵', color: '#4caf50' },
  'Virement': { label: 'Virement', icon: '🏦', color: '#2196f3' },
  'Bancontact': { label: 'Bancontact', icon: '💳', color: '#9c27b0' },
};

// ✅ Helper to format date for input[type="date"]
const formatDateForInput = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const OfferDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  
  // ✅ Get company info from Redux store (auto-fetches if needed)
  const { companyInfo } = useCompanyInfo();
  
  // ✅ Payment dialog state - using string for date
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [paymentDate, setPaymentDate] = useState(formatDateForInput(new Date()));
  const [paymentLoading, setPaymentLoading] = useState(false);

  // ✅ Define fetchData with useCallback to prevent infinite loops
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch offer
      const response = await getOffer(id);
      
      // ✅ Handle different response structures
      let offerData = null;
      if (response?.payload) {
        offerData = response.payload;
      } else if (response?.data) {
        offerData = response.data;
      } else {
        offerData = response;
      }


      // ✅ Validate that we have actual offer data
      if (!offerData || !offerData.Id) {
        throw new Error('Invalid offer data received');
      }

      const lineItems = offerData.LineItems || [];

      // ✅ Base amount (excl. VAT)
      const baseTotalExcl = lineItems.reduce(
        (sum, item) =>
          sum + (item.TotalExcl ?? (Number(item.Quantity || 0) * Number(item.UnitPrice || 0))),
        0
      );

      // ✅ Discount (absolute amount)
      const discountAmount = Number(offerData.Discount || 0);
      const amountAfterDiscount = baseTotalExcl - discountAmount;

      // ✅ VAT percentage
      const vatPercentage = Number(
        offerData.VATPercentage ?? (lineItems[0]?.VATPercent ?? lineItems[0]?.VAT ?? 21)
      );

      // ✅ VAT on discounted amount
      const totalVAT = (amountAfterDiscount * vatPercentage) / 100;

      // ✅ Final total (incl. VAT)
      const totalIncl = amountAfterDiscount + totalVAT;

      // ✅ Paid / due
      const offerPaidAmount = Number(offerData.OfferPaidAmount || 0);
      const offerDueAmount = offerData.OfferDueAmount != null
        ? Number(offerData.OfferDueAmount)
        : totalIncl - offerPaidAmount;

      const mappedOffer = {
        ...offerData,
        CustomerName: offerData.Customer?.Name || 'N/A',
        CustomerEmail: offerData.Customer?.Email || 'N/A',
        CustomerVAT: offerData.Customer?.VATNumber || 'N/A',
        CustomerPhone: offerData.Customer?.PhoneNumber || offerData.Customer?.MobilePhone || 'N/A',
        CustomerAddress: `${offerData.Customer?.Street || ''} ${offerData.Customer?.Box || ''}, ${offerData.Customer?.PostCode || ''} ${offerData.Customer?.City || ''}, ${offerData.Customer?.Country || ''}`.trim(),
        LineItems: lineItems.map((item) => ({
          ...item,
          ProductName: item.Product?.Name || `Product #${item.ProductId}`,
          ProductSKU: item.Product?.SKU || item.Product?.Reference || '',
          ProductDescription: item.Product?.Description || '',
          TotalExcl: item.TotalExcl ?? (Number(item.Quantity || 0) * Number(item.UnitPrice || 0)),
        })),
        // ✅ Calculated totals
        TotalExcl: baseTotalExcl,
        DiscountAmount: discountAmount,
        AmountAfterDiscount: amountAfterDiscount,
        VATPercentage: vatPercentage,
        TotalVAT: totalVAT,
        TotalIncl: totalIncl,
        OfferPaidAmount: offerPaidAmount,
        OfferDueAmount: offerDueAmount,
      };

      
      // ✅ Set offer data and stop loading immediately
      // Products are already included in LineItems, no need to fetch all products
      setOffer(mappedOffer);
      setLoading(false);
      

    } catch (error) {
      console.error('❌ Error fetching offer:', error);
      toast.error('Failed to load offer');
      setLoading(false);
    }
  }, [id]); // Only depends on id

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ✅ Get product details from offer LineItems (no external fetch needed)
  const getProductDetails = (productId) => {
    if (!productId || !offer?.LineItems) return null;
    const lineItem = offer.LineItems.find(item => item.ProductId === productId);
    return lineItem?.Product || null;
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // ✅ Get status step index
  const getStatusStep = (status) => {
    switch (status) {
      case OFFER_STATUS.DRAFT: return 0;
      case OFFER_STATUS.SENT: return 1;
      case OFFER_STATUS.ACCEPTED: return 2;
      case OFFER_STATUS.CONVERTED: return 3;
      case OFFER_STATUS.REJECTED: return -1;
      default: return 0;
    }
  };

  // ✅ Check if offer is fully paid
  const isFullyPaid = (offer) => {
    if (!offer) return false;
    return offer.OfferPaidAmount >= offer.TotalIncl;
  };

  // ✅ Get available status transitions - updated to check payment status
  const getAvailableStatuses = (currentStatus) => {
    const statuses = [];

    if (currentStatus === OFFER_STATUS.CONVERTED) {
      return [];
    }

    // ✅ Check if fully paid for conversion
    const canConvert = isFullyPaid(offer);

    if (currentStatus === OFFER_STATUS.DRAFT) {
      statuses.push({ value: OFFER_STATUS.SENT, label: 'Sent', icon: <SendIcon />, color: '#2196f3' });
      if (canConvert) {
        statuses.push({ value: 'CONVERT', label: 'Convert to Invoice', icon: <TransformIcon />, color: '#667eea' });
      } else {
        statuses.push({ 
          value: 'CONVERT_DISABLED', 
          label: 'Convert to Invoice (Not Fully Paid)', 
          icon: <TransformIcon />, 
          color: '#9e9e9e',
          disabled: true 
        });
      }
    }

    if (currentStatus === OFFER_STATUS.SENT) {
      statuses.push({ value: OFFER_STATUS.ACCEPTED, label: 'Accepted', icon: <CheckCircleIcon />, color: '#4caf50' });
      statuses.push({ value: OFFER_STATUS.REJECTED, label: 'Rejected', icon: <CancelIcon />, color: '#f44336' });
      if (canConvert) {
        statuses.push({ value: 'CONVERT', label: 'Convert to Invoice', icon: <TransformIcon />, color: '#667eea' });
      } else {
        statuses.push({ 
          value: 'CONVERT_DISABLED', 
          label: 'Convert to Invoice (Not Fully Paid)', 
          icon: <TransformIcon />, 
          color: '#9e9e9e',
          disabled: true 
        });
      }
    }

    if (currentStatus === OFFER_STATUS.ACCEPTED) {
      statuses.push({ value: OFFER_STATUS.REJECTED, label: 'Rejected', icon: <CancelIcon />, color: '#f44336' });
      if (canConvert) {
        statuses.push({ value: 'CONVERT', label: 'Convert to Invoice', icon: <TransformIcon />, color: '#667eea' });
      } else {
        statuses.push({ 
          value: 'CONVERT_DISABLED', 
          label: 'Convert to Invoice (Not Fully Paid)', 
          icon: <TransformIcon />, 
          color: '#9e9e9e',
          disabled: true 
        });
      }
    }

    if (currentStatus === OFFER_STATUS.REJECTED) {
      statuses.push({ value: OFFER_STATUS.SENT, label: 'Sent', icon: <SendIcon />, color: '#2196f3' });
      statuses.push({ value: OFFER_STATUS.ACCEPTED, label: 'Accepted', icon: <CheckCircleIcon />, color: '#4caf50' });
    }

    return statuses;
  };

  // ✅ Menu handlers
  const handleOpenStatusMenu = (event) => {
    setStatusMenuAnchor(event.currentTarget);
  };

  const handleCloseStatusMenu = () => {
    setStatusMenuAnchor(null);
  };

  // ✅ Handle convert to invoice
  const handleConvert = async () => {
    if (window.confirm('Convert this offer to an invoice? This cannot be undone.')) {
      try {
        const result = await convertOfferToInvoice(id);


        if (result?.status === "Failure" || result?.errorCode) {
          toast.error(result?.message || 'Failed to convert offer');
          return;
        }

        if (result?.status === "Success" && result?.payload) {
          const invoiceData = result.payload;
          toast.success(`Invoice #${invoiceData.InvoiceNumber || invoiceData.Id} created successfully!`);

          if (invoiceData.Id) {
            navigate(`/invoices/view/${invoiceData.Id}`);
          } else {
            navigate('/invoices');
          }
        } else if (result?.InvoiceId || result?.Id) {
          toast.success(`Invoice #${result.InvoiceNumber || result.InvoiceId || result.Id} created successfully!`);
          navigate(`/invoices/view/${result.InvoiceId || result.Id}`);
        } else {
          toast.success('Invoice created successfully!');
          navigate('/invoices');
        }

      } catch (error) {
        console.error('❌ Conversion error:', error);
        toast.error(error?.response?.data?.message || error.message || 'Failed to convert offer to invoice');
      } finally {
        handleCloseStatusMenu();
      }
    } else {
      handleCloseStatusMenu();
    }
  };

  // ✅ Handle status update
  const handleStatusUpdate = async (newStatus) => {
    try {
      await updateOfferStatus(id, newStatus);
      toast.success(`Offer status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      console.error('❌ Status update error:', error);
      toast.error(error?.response?.data?.message || 'Failed to update offer status');
    } finally {
      handleCloseStatusMenu();
    }
  };

  // ✅ Handle menu item click
  const handleStatusMenuClick = async (statusValue) => {
    if (statusValue === 'CONVERT_DISABLED') {
      toast.warning('Cannot convert to invoice. Offer must be fully paid first.');
      handleCloseStatusMenu();
      return;
    }
    
    if (statusValue === 'CONVERT') {
      handleConvert();
    } else {
      handleStatusUpdate(statusValue);
    }
  };

  // ✅ Handle open payment dialog
  const handleOpenPaymentDialog = () => {
    setPaymentAmount(offer.OfferDueAmount?.toFixed(2) || '0.00');
    setPaymentMethod(offer.PaymentMethod || 'Cash');
    setPaymentDate(formatDateForInput(new Date()));
    setPaymentDialogOpen(true);
  };

  // ✅ Handle close payment dialog
  const handleClosePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setPaymentAmount('');
    setPaymentDate(formatDateForInput(new Date()));
  };

  // ✅ Handle submit payment
  const handleSubmitPayment = async () => {
    const amount = parseFloat(paymentAmount);
    
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    
    if (amount > offer.OfferDueAmount) {
      toast.warning('Payment amount exceeds the due amount');
      return;
    }

    if (!paymentDate) {
      toast.error('Please select a payment date');
      return;
    }

    setPaymentLoading(true);
    
    try {
      // ✅ Prepare payment data according to backend DTO
      const paymentData = {
        OfferId: parseInt(id),
        PaymentAmount: amount,
        PaymentDate: new Date(paymentDate).toISOString(),
        PaymentMethod: paymentMethod,
      };
      
      
      const response = await recordOfferPayment(paymentData);
      
      
      if (response?.status === "Failure" || response?.errorCode) {
        toast.error(response?.message || 'Failed to record payment');
        return;
      }
      
      toast.success(`Payment of €${amount.toFixed(2)} recorded successfully!`);
      handleClosePaymentDialog();
      fetchData();
      
    } catch (error) {
      console.error('❌ Payment error:', error);
      
      if (error?.response?.status === 401) {
        toast.error('Unauthorized: Please log in again.');
      } else if (error?.response?.data?.message) {
        toast.error(`Error: ${error.response.data.message}`);
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('Failed to record payment. Please try again.');
      }
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/sales/offers/edit/${offer.Id}`);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this offer?')) {
      try {
        toast.success('Offer deleted successfully');
        navigate('/sales/offers');
      } catch (error) {
        toast.error('Failed to delete offer');
      }
    }
  };

  if (loading) return (
    <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={60} sx={{ color: '#667eea', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Loading offer details...
        </Typography>
      </Box>
    </Container>
  );

  if (!offer) return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Alert severity="error">
        <AlertTitle>Not Found</AlertTitle>
        Offer not found
      </Alert>
      <Button onClick={() => navigate('/sales/offers')} sx={{ mt: 2 }}>
        Back to Offers
      </Button>
    </Container>
  );

  const statusStep = getStatusStep(offer.Status);
  const availableStatuses = getAvailableStatuses(offer.Status);
  const paymentMethodInfo = PAYMENT_METHOD_OPTIONS[offer.PaymentMethod] || { label: offer.PaymentMethod || 'N/A', icon: '💳', color: '#9e9e9e' };
  const fullyPaid = isFullyPaid(offer);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DescriptionIcon sx={{ fontSize: 40, color: '#667eea', mr: 2 }} />
            <Box>
              <Typography variant="h4" fontWeight="700" sx={{ color: '#667eea' }}>
                Offer #{offer.OfferNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Created: {formatDate(offer.OfferDate || offer.CreatedDate)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/sales/offers')}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              Back
            </Button>

            {/* ✅ Make Payment Button */}
            {!fullyPaid && offer.Status !== OFFER_STATUS.CONVERTED && offer.Status !== OFFER_STATUS.REJECTED && (
              <Button
                variant="contained"
                color="success"
                startIcon={<PaymentsIcon />}
                onClick={handleOpenPaymentDialog}
                sx={{ 
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #43a047 0%, #1b5e20 100%)',
                  }
                }}
              >
                Make Payment (€{offer.OfferDueAmount?.toFixed(2)})
              </Button>
            )}

            {canEditOffer(offer.Status) && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                sx={{ borderRadius: 2 }}
              >
                Edit
              </Button>
            )}

            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              sx={{ borderRadius: 2 }}
            >
              Delete
            </Button>

            {/* ✅ Change Status Button */}
            {availableStatuses.length > 0 && (
              <>
                <Button
                  variant="contained"
                  endIcon={<KeyboardArrowDownIcon />}
                  onClick={handleOpenStatusMenu}
                  sx={{
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                    }
                  }}
                >
                  Change Status
                </Button>
                <Menu
                  anchorEl={statusMenuAnchor}
                  open={Boolean(statusMenuAnchor)}
                  onClose={handleCloseStatusMenu}
                  PaperProps={{
                    sx: { borderRadius: 2, minWidth: 250, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }
                  }}
                >
                  <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', color: 'text.secondary' }}>
                    Change Status To:
                  </Typography>
                  {availableStatuses.map((status) => (
                    <MenuItem
                      key={status.value}
                      onClick={() => handleStatusMenuClick(status.value)}
                      disabled={status.disabled}
                      sx={{ 
                        py: 1.5,
                        opacity: status.disabled ? 0.5 : 1,
                        '&.Mui-disabled': {
                          opacity: 0.5,
                        }
                      }}
                    >
                      <ListItemIcon sx={{ color: status.color }}>
                        {status.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={status.label}
                        primaryTypographyProps={{ 
                          fontWeight: 600, 
                          color: status.color,
                          fontSize: status.disabled ? '0.85rem' : '1rem'
                        }}
                      />
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </Box>
        </Box>
      </Paper>

      {/* ✅ Payment Dialog - Using native date input */}
      <Dialog 
        open={paymentDialogOpen} 
        onClose={handleClosePaymentDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <PaymentsIcon />
          Record Payment
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 2 }}>
          <Grid container spacing={3}>
            {/* Payment Summary */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: alpha('#667eea', 0.05), borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                    <Typography variant="h6" fontWeight={600}>€{offer?.TotalIncl?.toFixed(2)}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Already Paid</Typography>
                    <Typography variant="h6" fontWeight={600} color="success.main">€{offer?.OfferPaidAmount?.toFixed(2)}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Remaining</Typography>
                    <Typography variant="h6" fontWeight={600} color="error.main">€{offer?.OfferDueAmount?.toFixed(2)}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Payment Amount */}
            <Grid item xs={12}>
              <TextField
                label="Payment Amount"
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                fullWidth
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
                inputProps={{
                  min: 0.01,
                  max: offer?.OfferDueAmount,
                  step: 0.01
                }}
                helperText={`Maximum: €${offer?.OfferDueAmount?.toFixed(2)}`}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 2 }
                }}
              />
            </Grid>

            {/* ✅ Payment Date - Using native TextField type="date" */}
            <Grid item xs={12}>
              <TextField
                label="Payment Date"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                fullWidth
                required
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 2 }
                }}
              />
            </Grid>

            {/* Payment Method */}
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  label="Payment Method"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="Cash">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontSize="1.2rem">💵</Typography>
                      <Typography>Cash</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="Virement">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontSize="1.2rem">🏦</Typography>
                      <Typography>Virement</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="Bancontact">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontSize="1.2rem">💳</Typography>
                      <Typography>Bancontact</Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Quick Amount Buttons */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Quick Select
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setPaymentAmount(offer?.OfferDueAmount?.toFixed(2))}
                  sx={{ borderRadius: 2 }}
                >
                  Full Amount (€{offer?.OfferDueAmount?.toFixed(2)})
                </Button>
                {offer?.OfferDueAmount > 50 && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setPaymentAmount((offer?.OfferDueAmount / 2).toFixed(2))}
                    sx={{ borderRadius: 2 }}
                  >
                    50% (€{(offer?.OfferDueAmount / 2).toFixed(2)})
                  </Button>
                )}
                {offer?.OfferDueAmount > 100 && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setPaymentAmount('100.00')}
                    sx={{ borderRadius: 2 }}
                  >
                    €100.00
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleClosePaymentDialog} 
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitPayment}
            variant="contained"
            disabled={paymentLoading || !paymentAmount || parseFloat(paymentAmount) <= 0 || !paymentDate}
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #43a047 0%, #1b5e20 100%)',
              }
            }}
          >
            {paymentLoading ? 'Processing...' : `Record Payment €${parseFloat(paymentAmount || 0).toFixed(2)}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Payment Warning Alert */}
      {!fullyPaid && offer.Status !== OFFER_STATUS.CONVERTED && offer.Status !== OFFER_STATUS.REJECTED && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3, borderRadius: 2 }}
          icon={<PaymentIcon />}
        >
          <AlertTitle sx={{ fontWeight: 700 }}>⚠ Payment Required</AlertTitle>
          This offer is not fully paid. You must receive full payment (€{offer.TotalIncl?.toFixed(2)}) before converting to an invoice.
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Paid:</strong> €{offer.OfferPaidAmount?.toFixed(2)} | <strong>Remaining:</strong> €{offer.OfferDueAmount?.toFixed(2)}
          </Typography>
        </Alert>
      )}

      {/* ✅ Fully Paid Success Alert */}
      {fullyPaid && offer.Status !== OFFER_STATUS.CONVERTED && (
        <Alert 
          severity="success" 
          sx={{ mb: 3, borderRadius: 2 }}
          icon={<CheckCircleIcon />}
        >
          <AlertTitle sx={{ fontWeight: 700 }}>✓ Fully Paid</AlertTitle>
          This offer has been fully paid. You can now convert it to an invoice.
        </Alert>
      )}

      {/* Status Stepper */}
      {offer.Status !== OFFER_STATUS.REJECTED && (
        <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
          <Typography variant="h6" fontWeight="700" sx={{ mb: 3, color: '#667eea' }}>
            Offer Progress
          </Typography>
          <Stepper alternativeLabel activeStep={statusStep} connector={<ColorlibConnector />}>
            {['Draft', 'Sent', 'Accepted', 'Converted'].map((label, index) => (
              <Step key={label} completed={statusStep > index}>
                <StepLabel StepIconComponent={ColorlibStepIcon}>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>
      )}

      {/* Rejected Alert */}
      {offer.Status === OFFER_STATUS.REJECTED && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>✗ Offer Rejected</AlertTitle>
          This offer has been rejected by the customer.
        </Alert>
      )}

      {/* Converted Alert */}
      {offer.Status === OFFER_STATUS.CONVERTED && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>✓ Converted to Invoice</AlertTitle>
          This offer has been converted to an invoice.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Company Info */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <BusinessIcon sx={{ color: '#667eea', mr: 1 }} />
              <Typography variant="h6" fontWeight="700" sx={{ color: '#667eea' }}>
                Company Information
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                <Typography variant="body1" fontWeight={500}>{companyInfo?.name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">VAT Number</Typography>
                <Typography variant="body1">{companyInfo?.vat || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                <Typography variant="body1">{companyInfo?.email || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                <Typography variant="body1">{companyInfo?.phone || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                <Typography variant="body1">
                  {companyInfo?.address || 'N/A'}
                  {companyInfo?.postCode && `, ${companyInfo.postCode}`}
                  {companyInfo?.city && ` ${companyInfo.city}`}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">IBAN</Typography>
                <Typography variant="body1">{companyInfo?.iban || 'N/A'}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Customer Info */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PersonIcon sx={{ color: '#667eea', mr: 1 }} />
              <Typography variant="h6" fontWeight="700" sx={{ color: '#667eea' }}>
                Customer Information
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                <Typography variant="body1" fontWeight={500}>{offer.CustomerName}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">VAT Number</Typography>
                <Typography variant="body1">{offer.CustomerVAT}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                <Typography variant="body1">{offer.CustomerEmail}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                <Typography variant="body1">{offer.CustomerPhone}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                <Typography variant="body1">{offer.CustomerAddress}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Offer Summary */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', height: '100%' }}>
            <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
              Offer Summary
            </Typography>
            <Grid container spacing={2}>
              {/* Status */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Current Status</Typography>
                <Chip
                  label={getStatusLabel(offer.Status)}
                  sx={{
                    mt: 1,
                    backgroundColor: getStatusColor(offer.Status),
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    px: 2,
                    py: 2.5,
                  }}
                />
              </Grid>

              {/* Payment Status */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Payment Status</Typography>
                <Chip
                  label={fullyPaid ? '✓ Fully Paid' : '⚠ Not Fully Paid'}
                  sx={{
                    mt: 1,
                    backgroundColor: fullyPaid ? '#4caf50' : '#ff9800',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    px: 2,
                    py: 2.5,
                  }}
                />
              </Grid>

              {/* Payment Method */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Payment Method</Typography>
                <Chip
                  icon={<Typography sx={{ fontSize: '1rem', ml: 1 }}>{paymentMethodInfo.icon}</Typography>}
                  label={paymentMethodInfo.label}
                  sx={{
                    mt: 1,
                    backgroundColor: alpha(paymentMethodInfo.color, 0.15),
                    color: paymentMethodInfo.color,
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    px: 1,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              {/* Subtotal */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: alpha('#667eea', 0.05), borderRadius: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Subtotal (Excl. VAT)</Typography>
                  <Typography variant="h6" fontWeight={600}>€{offer.TotalExcl?.toFixed(2) || '0.00'}</Typography>
                </Box>
              </Grid>

              {/* Discount */}
              {offer.DiscountAmount > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: alpha('#ff9800', 0.1), borderRadius: 2, border: `1px solid ${alpha('#ff9800', 0.3)}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DiscountIcon sx={{ color: '#ff9800', fontSize: 20 }} />
                      <Typography variant="subtitle2" color="warning.main">Discount</Typography>
                    </Box>
                    <Typography variant="h6" fontWeight={600} color="warning.main">
                      -€{offer.DiscountAmount?.toFixed(2) || '0.00'}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* After Discount */}
              {offer.DiscountAmount > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: alpha('#667eea', 0.05), borderRadius: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">After Discount</Typography>
                    <Typography variant="h6" fontWeight={600}>€{offer.AmountAfterDiscount?.toFixed(2) || '0.00'}</Typography>
                  </Box>
                </Grid>
              )}

              {/* VAT */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: alpha('#667eea', 0.05), borderRadius: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">VAT ({offer.VATPercentage || 0}%)</Typography>
                  <Typography variant="h6" fontWeight={600}>€{offer.TotalVAT?.toFixed(2) || '0.00'}</Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              {/* Total */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: alpha('#667eea', 0.15), borderRadius: 2, border: `1px solid ${alpha('#667eea', 0.3)}` }}>
                  <Typography variant="subtitle1" fontWeight={700} color="primary">Total (Incl. VAT)</Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#667eea' }}>
                    €{offer.TotalIncl?.toFixed(2) || '0.00'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              {/* Paid Amount */}
              <Grid item xs={6}>
                <Box sx={{ p: 1.5, bgcolor: alpha('#4caf50', 0.1), borderRadius: 2, border: `1px solid ${alpha('#4caf50', 0.3)}` }}>
                  <Typography variant="subtitle2" color="text.secondary">Paid Amount</Typography>
                  <Typography variant="h6" fontWeight={600} sx={{ color: '#4caf50' }}>
                    €{offer.OfferPaidAmount?.toFixed(2) || '0.00'}
                  </Typography>
                </Box>
              </Grid>

              {/* Due Amount */}
              <Grid item xs={6}>
                <Box sx={{ p: 1.5, bgcolor: alpha('#f44336', 0.1), borderRadius: 2, border: `1px solid ${alpha('#f44336', 0.3)}` }}>
                  <Typography variant="subtitle2" color="text.secondary">Due Amount</Typography>
                  <Typography variant="h6" fontWeight={600} sx={{ color: '#f44336' }}>
                    €{offer.OfferDueAmount?.toFixed(2) || '0.00'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Line Items */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
            <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
              Line Items
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Unit Price</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">VAT %</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Total (Excl.)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {offer.LineItems?.length > 0 ? (
                    offer.LineItems.map((item, index) => {
                      const productDetails = getProductDetails(item.ProductId);
                      const productName = productDetails?.Name || item.ProductName || `Product #${item.ProductId}`;
                      const productSKU = productDetails?.Reference || item.ProductSKU || '';
                      const productDescription = productDetails?.Description || item.ProductDescription || '';

                      return (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Typography fontWeight={500}>{productName}</Typography>
                            {productDescription && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {productDescription}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {productSKU ? (
                              <Chip label={productSKU} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                            ) : 'N/A'}
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={item.Quantity || 0} size="small" color="info" sx={{ fontWeight: 600 }} />
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={500}>€{Number(item.UnitPrice || 0).toFixed(2)}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={`${item.VATPercent || item.VAT || offer.VATPercentage || 0}%`}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: '#667eea' }}>
                            €{Number(item.TotalExcl || 0).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ color: '#888', py: 4 }}>
                        No line items found
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Totals Rows */}
                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.05)' }}>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                      Subtotal (Excl. VAT):
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: '#667eea' }}>
                      €{offer.TotalExcl?.toFixed(2) || '0.00'}
                    </TableCell>
                  </TableRow>

                  {offer.DiscountAmount > 0 && (
                    <TableRow sx={{ bgcolor: 'rgba(255, 152, 0, 0.08)' }}>
                      <TableCell colSpan={5} align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: '#ff9800' }}>
                        Discount:
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: '#ff9800' }}>
                        -€{offer.DiscountAmount?.toFixed(2) || '0.00'}
                      </TableCell>
                    </TableRow>
                  )}

                  {offer.DiscountAmount > 0 && (
                    <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.05)' }}>
                      <TableCell colSpan={5} align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                        After Discount:
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: '#667eea' }}>
                        €{offer.AmountAfterDiscount?.toFixed(2) || '0.00'}
                      </TableCell>
                    </TableRow>
                  )}

                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.05)' }}>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                      VAT ({offer.VATPercentage || 0}%):
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: '#667eea' }}>
                      €{offer.TotalVAT?.toFixed(2) || '0.00'}
                    </TableCell>
                  </TableRow>

                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.15)' }}>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                      Total (Incl. VAT):
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#667eea' }}>
                      €{offer.TotalIncl?.toFixed(2) || '0.00'}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Notes Section */}
        {(offer.CustomText || offer.Notes) && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
              <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
                Additional Information
              </Typography>
              <Grid container spacing={3}>
                {offer.CustomText && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Custom Text</Typography>
                    <Paper sx={{ p: 2, bgcolor: alpha('#667eea', 0.05), borderRadius: 2 }}>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {offer.CustomText}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                {offer.Notes && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Internal Notes</Typography>
                    <Paper sx={{ p: 2, bgcolor: alpha('#ff9800', 0.05), borderRadius: 2 }}>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {offer.Notes}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default OfferDetailPage;